import React, { useRef, useCallback } from 'react';
import { Camera, Loader2 } from 'lucide-react';
import { useProfilePhoto } from '@/hooks/useProfilePhoto';
import { useToast } from '@/components/ui/use-toast';
import ProfilePhotoDisplay from './ProfilePhotoDisplay';
import { ProfilePhotoUploadProps } from './ProfilePhotoUpload.types';

const ProfilePhotoUpload: React.FC<ProfilePhotoUploadProps> = ({
  currentPhotoUrl,
  onPhotoUpdate,
  onError,
  className = '',
  size = 'lg'
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const { state, uploadPhoto, clearError } = useProfilePhoto();

  const handleFileSelect = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    clearError();

    const result = await uploadPhoto(file);
    
    if (result.success && result.url) {
      onPhotoUpdate(result.url);
      toast({
        title: 'Photo uploaded successfully!',
        description: 'Your profile photo has been updated.',
        variant: 'default'
      });
    } else {
      const errorMessage = result.error || 'Failed to upload photo';
      onError(errorMessage);
      toast({
        title: 'Upload failed',
        description: errorMessage,
        variant: 'destructive'
      });
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [uploadPhoto, onPhotoUpdate, onError, clearError, toast]);

  const handleUploadClick = useCallback(() => {
    if (state.isUploading || state.isDeleting) return;
    fileInputRef.current?.click();
  }, [state.isUploading, state.isDeleting]);


  const isLoading = state.isUploading || state.isDeleting;

  return (
    <div className={`relative ${className}`}>
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png"
        onChange={handleFileSelect}
        className="hidden"
        disabled={isLoading}
      />

      {/* Clickable overlay for upload */}
      <div 
        className="absolute inset-0 cursor-pointer z-30"
        onClick={handleUploadClick}
        title="Click to upload photo"
      />

      {/* Loading overlay */}
      {isLoading && (
        <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center z-40">
          <Loader2 className="w-6 h-6 text-white animate-spin" />
        </div>
      )}

      {/* Action buttons */}
      {!isLoading && (
        <div className="absolute -bottom-1 -right-1 flex gap-1 z-30">
          {/* Upload button */}
          <button
            onClick={handleUploadClick}
            className="w-6 h-6 bg-[#D72E16] text-white rounded-full flex items-center justify-center hover:bg-[#B91C1C] transition-colors"
            title="Upload photo"
          >
            <Camera className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* Error message */}
      {state.error && (
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 text-sm text-red-500 text-center whitespace-nowrap z-50">
          {state.error}
        </div>
      )}

      {/* Progress indicator */}
      {state.isUploading && state.progress > 0 && (
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 z-50">
          <div className="w-20 bg-gray-200 rounded-full h-1">
            <div 
              className="bg-[#D72E16] h-1 rounded-full transition-all duration-300"
              style={{ width: `${state.progress}%` }}
            />
          </div>
          <p className="text-xs text-gray-500 text-center mt-1">
            {Math.round(state.progress)}%
          </p>
        </div>
      )}
    </div>
  );
};

export default ProfilePhotoUpload;

